import { DailyDish } from "@/redux/services/dailyDishes/dailyDishes.type";
import {
  formatPrice,
  convertPriceFromCent,
  getImageUrl,
  formatTimeDisplay,
  getDayAbbr,
} from "@/lib/utils";
import { useAppSelector } from "@/redux/features/hooks";
import { useRestaurantCurrency } from "@/lib/hooks";
import { Clock } from "lucide-react";

interface Props {
  dailyDish: DailyDish;
  openDailyDishModal: (dailyDish: DailyDish) => void;
}

const DailyDishCard = ({ dailyDish, openDailyDishModal }: Props) => {
  const currentRestaurant = useAppSelector(
    (state) => state.user.currentRestaurant
  );
  const currency = useRestaurantCurrency();

  return (
    <div
      className={`flex flex-col items-center bg-card border border-border p-4 rounded-lg cursor-pointer hover:shadow-md transition-all relative ${
        !dailyDish.isActive ? "opacity-75" : ""
      }`}
      onClick={() => openDailyDishModal(dailyDish)}
    >
      <div className="w-28 h-28 sm:w-32 sm:h-32 overflow-hidden rounded-full border-2 border-border mb-3 relative">
        <img
          src={getImageUrl(
            dailyDish.image?.url ?? currentRestaurant?.image?.url
          )}
          alt={dailyDish.name}
          className={`w-full h-full object-cover ${
            !dailyDish.isActive ? "grayscale" : ""
          }`}
        />
        {!dailyDish.isActive && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-full">
            <span className="text-white text-xs font-medium px-2 py-0.5 bg-destructive rounded">
              Inactif
            </span>
          </div>
        )}
        {dailyDish.isCurrentlyActive && dailyDish.isActive && (
          <div className="absolute inset-0 bg-accent/20 flex items-center justify-center rounded-full">
            <span className="text-accent text-xs font-medium px-2 py-0.5 bg-accent-light rounded">
              Disponible
            </span>
          </div>
        )}
      </div>

      <h3 className="font-semibold text-center text-card-foreground mb-1">
        {dailyDish.name}
      </h3>

      <div className="text-primary font-bold text-base mb-2">
        {formatPrice(convertPriceFromCent(dailyDish.price), currency)}
      </div>

      {/* Days badges */}
      <div className="flex flex-wrap gap-1 justify-center mb-2">
        {dailyDish.daysOfWeek.slice(0, 3).map((day) => (
          <span
            key={day}
            className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-muted text-muted-foreground"
          >
            {getDayAbbr(day)}
          </span>
        ))}
        {dailyDish.daysOfWeek.length > 3 && (
          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
            +{dailyDish.daysOfWeek.length - 3}
          </span>
        )}
      </div>

      {/* Time display */}
      {(dailyDish.startTime || dailyDish.endTime) && (
        <div className="text-xs text-muted-foreground flex items-center gap-1">
          <Clock className="h-3 w-3" />
          {formatTimeDisplay(dailyDish.startTime)} -{" "}
          {formatTimeDisplay(dailyDish.endTime)}
        </div>
      )}
    </div>
  );
};

export const DailyDishCardSkeleton = () => {
  return (
    <div className="flex flex-col items-center bg-card border border-border p-4 rounded-lg animate-pulse">
      <div className="w-28 h-28 sm:w-32 sm:h-32 bg-gray-200 rounded-full mb-3" />
      <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
      <div className="h-5 bg-gray-200 rounded w-1/2 mb-2" />
      <div className="flex gap-1 mb-2">
        <div className="h-4 w-8 bg-gray-200 rounded" />
        <div className="h-4 w-8 bg-gray-200 rounded" />
        <div className="h-4 w-8 bg-gray-200 rounded" />
      </div>
      <div className="h-3 bg-gray-200 rounded w-1/3" />
    </div>
  );
};

export default DailyDishCard;
